import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, ShoppingBag } from 'lucide-react';
import api from '../lib/api.js';
import { formatCurrency } from '../lib/format.js';

/**
 * Input de recherche de commandes Shopify avec debounce 250ms.
 *
 * Props :
 *   value          — id de la commande sélectionnée (string | null)
 *   onChange(id)   — appelé avec l'id sélectionné ou null
 *   selectedOrder  — objet commande actuel { id, name, total_cents, currency, customer_email }
 *                    ou null (pour afficher le nom courant sans re-fetch)
 */
export default function ShopifyOrderAutocomplete({ value, onChange, selectedOrder }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);

  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceTimer = useRef(null);

  // Fermeture sur clic extérieur
  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounce + fetch
  const search = useCallback((q) => {
    clearTimeout(debounceTimer.current);
    if (!q || q.length < 1) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api.get(
          `/shopify/orders/search?q=${encodeURIComponent(q)}&limit=20`
        );
        setResults(Array.isArray(data) ? data : []);
        setOpen(true);
        setHighlighted(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  }, []);

  function handleInputChange(e) {
    const q = e.target.value;
    setQuery(q);
    search(q);
  }

  function selectOrder(order) {
    onChange(order.id);
    setQuery('');
    setOpen(false);
    setResults([]);
  }

  function clearSelection(e) {
    e.stopPropagation();
    onChange(null);
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  }

  const handleKeyDown = useCallback(
    (e) => {
      if (!open) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlighted((h) => Math.min(h + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlighted((h) => Math.max(h - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[highlighted]) selectOrder(results[highlighted]);
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
      }
    },
    [open, results, highlighted] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Si une commande est déjà sélectionnée, on affiche son nom
  const displayName = value && selectedOrder
    ? `${selectedOrder.name} — ${formatCurrency(selectedOrder.total_cents)}`
    : null;

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Affichage de la commande sélectionnée */}
      {value && displayName ? (
        <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm">
          <ShoppingBag size={14} className="text-slate-400 shrink-0" aria-hidden />
          <span className="flex-1 truncate text-slate-900">{displayName}</span>
          <button
            type="button"
            onClick={clearSelection}
            className="shrink-0 rounded p-1 text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-400 min-h-[40px] min-w-[40px] flex items-center justify-center"
            aria-label="Supprimer la commande liée"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            aria-hidden
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (results.length > 0) setOpen(true);
            }}
            placeholder="Rechercher une commande (#1234)…"
            className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 transition-colors"
            aria-label="Rechercher une commande Shopify"
            autoComplete="off"
          />
          {loading && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
              …
            </span>
          )}
        </div>
      )}

      {/* Dropdown */}
      {open && results.length > 0 && (
        <ul
          className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden max-h-64 overflow-y-auto"
          role="listbox"
          aria-label="Commandes Shopify"
        >
          {results.map((order, idx) => {
            const isHighlighted = idx === highlighted;
            return (
              <li
                key={order.id}
                role="option"
                aria-selected={isHighlighted}
                onMouseEnter={() => setHighlighted(idx)}
                onClick={() => selectOrder(order)}
                className={`flex flex-col cursor-pointer px-3 py-2.5 text-sm border-l-2 min-h-[40px] justify-center ${
                  isHighlighted
                    ? 'bg-slate-100 border-l-slate-400'
                    : 'border-l-transparent hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-900">{order.name}</span>
                  <span className="text-slate-600 tabular-nums shrink-0">
                    {formatCurrency(order.total_cents)}
                  </span>
                </div>
                {order.customer_email && (
                  <span className="text-xs text-slate-400 truncate">{order.customer_email}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {open && query.length >= 1 && !loading && results.length === 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg px-3 py-3 text-sm text-slate-400 text-center">
          Aucune commande trouvée
        </div>
      )}
    </div>
  );
}
